import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { searchProps, useSearch } from '../../hooks/useSearch'
import { paginationProps, usePagination } from '../../hooks/usePagination'
import { Input } from '../../components/ui/adminComponents/Input'
import { LoaderPoints } from '../../components/ui/LoaderPoints'
import { Table } from '../../components/ui/adminComponents/Table'
import { AppStore } from '../../store/applicationState'
import { GetGeneralActivity } from '../../store/admin/statistics/generalActivity/actions'
import { getGeneralActivityParsed } from '../../utils/statisticsUtils/statisticDataHelpers'

interface Props {
  currentTenderId: number
}

const limitAmount = 400
const initialParams = { limit: limitAmount, offset: 0, search: undefined, sort: '', sortBy: '' }

const generalActivityThead = {
  stock_number: 'Stock #',
  title: 'Title',
  count_of_unique_user_views: 'Uniq views by customers',
  count_of_wishlists: 'Uniq favorites by customers',
  count_of_bids: 'Number of bids',
}

const tableCellWidth = [120, 270, 240, 240, 160]

export const GeneralActivity: React.FC<Props> = ({ currentTenderId }) => {
  const [params, setParams] = useState<searchProps | paginationProps>(initialParams)
  const [dispatchTime, setDispatchTime] = useState(0)
  const [childIndex, setChildIndex] = useState<number[]>([])
  const dispatch = useDispatch()
  const { tenders, generalActivity } = useSelector((store: AppStore) => store)

  const didMount = useRef(false)

  usePagination(generalActivity.data, setParams, limitAmount)
  useSearch(limitAmount, params, setParams, setDispatchTime)

  useEffect(() => {
    if (didMount.current) {
      setDispatchTime((prev) => prev + 1)
      setParams(initialParams)
    }
    didMount.current = true
  }, [currentTenderId])

  useEffect(() => {
    dispatch(
      GetGeneralActivity.request({
        idTender: currentTenderId,
        offset: params.offset,
        limit: params.limit,
        search: params.search,
        sortBy: params.sortBy,
        sort: params.sort,
      })
    )
  }, [params.offset, params.limit, params.sortBy, params.sort, dispatchTime])

  useEffect(() => {
    if (tenders.updateTenderNow) {
      if (params.offset === 0) setDispatchTime((prev) => prev + 1)
      else setParams((prev) => ({ ...prev, offset: 0, limit: prev.limit + prev.offset }))
    }
  }, [tenders.updateTenderNow])

  /// /////////////////////////////////////////////////////////////////////////////////////////////
  const callBack = (sortBy: string) => {
    if (sortBy === 'action') return

    let value: { sortBy: string; sort: 'ASC' | 'DESC' }
    if (sortBy !== params.sortBy || params.sort === 'DESC') value = { sortBy, sort: 'ASC' }
    else value = { sortBy, sort: 'DESC' }

    setParams((prev) => ({ ...prev, offset: 0, limit: limitAmount, sortBy: value.sortBy, sort: value.sort }))
    setChildIndex([])
  }

  const generalActivityValues = useMemo(() => {
    return generalActivity.data ? getGeneralActivityParsed(generalActivity.data, currentTenderId) : null
  }, [generalActivity.data, childIndex])

  useEffect(() => {
    setChildIndex(
      childIndex.filter(
        (child) =>
          generalActivity.data && generalActivity.data.some((item) => +`${currentTenderId}${item.user_id}` === child)
      )
    )
  }, [generalActivity.data])

  const generalActivityTable = useMemo(() => {
    return generalActivityValues ? (
      <Table
        thead={generalActivityThead}
        tbody={generalActivityValues}
        callBack={callBack}
        childIndex={childIndex}
        setChildIndex={setChildIndex}
        tableCellWidth={tableCellWidth}
        isStatisticsTable
      />
    ) : null
  }, [generalActivityValues])

  return (
    <>
      <div className='flex justify-between lg:items-center mb-4 flex-col lg:flex-row'>
        <div className='w-full md:w-1/3 lg:w-1/4 mb-6 lg:mb-0'>
          <Input
            placeholder='Search...'
            value={params.search || ''}
            onChange={(e) => setParams((prev) => ({ ...prev, search: e.target.value }))}
          />
        </div>
      </div>
      <div className='relative'>
        {generalActivityTable}
        {!!generalActivityTable && generalActivity.loading && (
          <div className='absolute left-0 right-0 -bottom-6 flex justify-center'>
            <LoaderPoints className='m-auto' />
          </div>
        )}
      </div>
    </>
  )
}
